if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');
        this.classList.add('pairfit-quick-view-modal--loading');
        this.modalContent.innerHTML = `
          <div class="pairfit-quick-view-modal__loading">
            <span class="pairfit-spinner" aria-hidden="true"><span class="pairfit-spinner__ring"></span></span>
          </div>
        `;

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
            this.classList.remove('pairfit-quick-view-modal--loading');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.remove('pairfit-product-page');
        productElement.classList.add('pairfit-quick-view-product');

        productElement.querySelector('.product__description')?.remove();
        productElement.querySelector('share-button')?.remove();
        productElement.querySelector('.complementary-products')?.remove();
        productElement.querySelectorAll('.product__accordion').forEach((accordion) => accordion.remove());
        productElement.querySelectorAll('.product__text').forEach((text) => text.remove());
        productElement.querySelector('.pairfit-product-note')?.remove();
        productElement.querySelector('.shopify-payment-button')?.remove();
        productElement.querySelector('.pairfit-product-whatsapp__button')?.remove();
        productElement.querySelector('.pairfit-buy-now')?.remove();
        productElement.querySelector('.pairfit-buy-reassure')?.remove();
        productElement.querySelector('.pairfit-pdp-offer')?.remove();
        productElement.querySelector('.pairfit-product-trust')?.remove();
        productElement.querySelector('[data-pairfit-sticky-atc]')?.remove();

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.enhanceVariantPickers(productElement);
        this.enhanceProductGallery(productElement);
        this.relocateViewDetailsLink(productElement);
      }

      relocateViewDetailsLink(productElement) {
        const viewDetails = productElement.querySelector('.product__view-details');
        const buttonsContainer = productElement.querySelector('.product-form__buttons');
        if (!viewDetails || !buttonsContainer) return;

        buttonsContainer.appendChild(viewDetails);
      }

      enhanceProductGallery(productElement) {
        const mediaGallery = productElement.querySelector('media-gallery');
        if (mediaGallery) mediaGallery.classList.add('pairfit-qv-gallery');

        productElement.querySelectorAll('.product__media img').forEach((img) => {
          img.setAttribute('sizes', '(min-width: 750px) 360px, 78vw');
        });
      }

      enhanceVariantPickers(productElement) {
        productElement
          .querySelectorAll('.product-form__input--pill, .product-form__input--swatch')
          .forEach((fieldset) => {
            if (fieldset.querySelector('.pairfit-qv-variant-options')) return;

            const wrapper = document.createElement('div');
            wrapper.className = 'pairfit-qv-variant-options';

            fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
              const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
              if (!label) return;
              wrapper.appendChild(input);
              wrapper.appendChild(label);
            });

            if (wrapper.childElementCount) fieldset.appendChild(wrapper);
          });
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        productElement.querySelectorAll('modal-dialog').forEach((modal) => {
          if (modal.classList.contains('pairfit-size-chart-modal')) return;
          modal.remove();
        });

        if (productElement.querySelector('.pairfit-size-guide-inline')) {
          productElement.querySelectorAll('.pairfit-size-guide-link').forEach((link) => link.remove());
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
